/**
 * Distinction régionale du néerlandais : Flandre (nl-BE) vs Pays-Bas (nl-NL).
 * JSON i18n partagés sous `locales/nl/` ; la région pilote géo + SEO.
 *
 * @see docs/seo-international-pro/08-MARKET-HUBS-BE.md
 */

export const DUTCH_REGIONAL_CODES = ['nl-BE', 'nl-NL'] as const;
export type DutchRegionalCode = (typeof DUTCH_REGIONAL_CODES)[number];

export type DutchRegion = 'BE' | 'NL';

export const DUTCH_LOCALE_META: Record<
  DutchRegionalCode,
  {
    region: DutchRegion;
    resourceLang: 'nl';
    flag: string;
    name: string;
    geoCountry: 'be' | 'nl';
    seoRoutePrefix: '/nl' | null;
    ogLocale: 'nl_BE' | 'nl_NL';
    hreflang: 'nl-BE' | 'nl-NL';
    marketContentPath: string;
  }
> = {
  'nl-BE': {
    region: 'BE',
    resourceLang: 'nl',
    flag: '🇧🇪',
    name: 'Nederlands (België)',
    geoCountry: 'be',
    seoRoutePrefix: null,
    ogLocale: 'nl_BE',
    hreflang: 'nl-BE',
    marketContentPath: '/be/nl',
  },
  'nl-NL': {
    region: 'NL',
    resourceLang: 'nl',
    flag: '🇳🇱',
    name: 'Nederlands (Nederland)',
    geoCountry: 'nl',
    seoRoutePrefix: '/nl',
    ogLocale: 'nl_NL',
    hreflang: 'nl-NL',
    marketContentPath: '/nl',
  },
};

export function isDutchRegionalCode(code: string | null | undefined): code is DutchRegionalCode {
  return code === 'nl-BE' || code === 'nl-NL';
}

export function normalizeDutchRegionalCode(
  raw: string | null | undefined
): DutchRegionalCode | null {
  if (!raw) return null;
  const lower = raw.trim().replace('_', '-').toLowerCase();
  if (lower === 'nl-nl') return 'nl-NL';
  if (lower === 'nl-be' || lower === 'nl') return 'nl-BE';
  return null;
}

export function dutchResourceLanguage(code: string): 'nl' | null {
  if (isDutchRegionalCode(code) || code === 'nl') return 'nl';
  return normalizeDutchRegionalCode(code) ? 'nl' : null;
}
